import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Building2, Ban, Play, Trash2, Users as UsersIcon, Check, X, Clock } from "lucide-react";
import { T } from "@/lib/testIds";

export default function PlatformWorkspaces() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState(
    new URLSearchParams(window.location.search).get("tab") === "pending" ? "pending" : "all"
  );

  const load = async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([
        api.get("/platform/workspaces"),
        api.get("/platform/stats"),
      ]);
      setRows(w.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load platform data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleSuspend = async (row) => {
    setBusy(row.id);
    try {
      await api.patch(`/platform/workspaces/${row.id}/suspend`, null, {
        params: { suspended: !row.suspended },
      });
      toast.success(!row.suspended ? "Workspace suspended" : "Workspace reactivated");
      load();
    } catch { toast.error("Action failed"); }
    finally { setBusy(null); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Permanently delete workspace "${row.name}"?\nThis cascades to all its clients, members, invites, and payments.`)) return;
    setBusy(row.id);
    try {
      await api.delete(`/platform/workspaces/${row.id}`);
      toast.success("Workspace deleted");
      setRows((r) => r.filter((x) => x.id !== row.id));
    } catch { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  const approve = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/platform/workspaces/${row.id}/approve`);
      toast.success(`Approved · owner emailed`);
      load();
    } catch { toast.error("Approve failed"); }
    finally { setBusy(null); }
  };

  const reject = async (row) => {
    if (!window.confirm(`Reject "${row.name}"? The owner will get an email.`)) return;
    setBusy(row.id);
    try {
      await api.post(`/platform/workspaces/${row.id}/reject`);
      toast.success(`Rejected · owner emailed`);
      load();
    } catch { toast.error("Reject failed"); }
    finally { setBusy(null); }
  };

  const visibleRows = rows.filter((w) => w.business_type !== "platform").filter((w) => {
    if (tab === "pending") return (w.approval_status || "approved") === "pending";
    return true;
  });
  const pendingCount = rows.filter(
    (w) => w.business_type !== "platform" && (w.approval_status || "approved") === "pending"
  ).length;

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5">
        <div className="co-label">Platform console</div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">Workspaces</h1>
      </header>
      <div className="p-6 lg:p-10 space-y-6">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 border co-hairline bg-white">
            {[
              { k: "workspaces", label: "Workspaces" },
              { k: "workspaces_suspended", label: "Suspended" },
              { k: "users", label: "Users" },
              { k: "users_suspended", label: "Suspended users" },
              { k: "clients", label: "Total clients" },
              { k: "payments_pending", label: "Pending reviews" },
            ].map((s, i) => (
              <div key={s.k} className={`p-5 ${i < 5 ? "border-r co-hairline" : ""}`}>
                <div className="co-label">{s.label}</div>
                <div className="font-heading text-2xl font-bold tracking-tighter mt-1">
                  {stats[s.k]}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border co-hairline bg-white overflow-x-auto">
          <div className="flex items-center border-b co-hairline">
            {[
              { k: "all", label: `All workspaces`, count: rows.filter((w) => w.business_type !== "platform").length },
              { k: "pending", label: `Pending approval`, count: pendingCount, icon: Clock },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setTab(t.k)}
                data-testid={`platform-tab-${t.k}`}
                className={`px-5 py-3 text-sm font-semibold border-r co-hairline flex items-center gap-2 transition-colors duration-200 ${
                  tab === t.k ? "bg-[#0A0A0B] text-white" : "text-[#52525B] hover:bg-[#FAFAFA]"
                }`}
              >
                {t.icon && <t.icon className="h-3.5 w-3.5" strokeWidth={2} />}
                {t.label}
                <span className={`text-xs font-mono ${tab === t.k ? "text-white/70" : "text-[#A1A1AA]"}`}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>
          <table className="co-table">
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Owner</th>
                <th>Type</th>
                <th>Members</th>
                <th>Clients</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center text-[#52525B]">Loading…</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-[#52525B] py-10">
                  {tab === "pending" ? "No workspaces awaiting approval." : "No workspaces yet."}
                </td></tr>
              ) : visibleRows.map((w) => {
                const isPending = (w.approval_status || "approved") === "pending";
                const isRejected = (w.approval_status || "approved") === "rejected";
                return (
                <tr key={w.id} data-testid={T.platformWsRow}>
                  <td>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} />
                      <span className="font-semibold">{w.name}</span>
                    </div>
                    <div className="text-xs font-mono text-[#52525B]">{w.id.slice(0, 8)}…</div>
                  </td>
                  <td>
                    <div className="text-sm">{w.owner?.name || "—"}</div>
                    <div className="text-xs text-[#52525B]">{w.owner?.email || "—"}</div>
                  </td>
                  <td className="capitalize text-[#52525B]">{w.business_type}</td>
                  <td className="font-mono">{w.members_count}</td>
                  <td className="font-mono">{w.clients_count}</td>
                  <td>
                    {isPending ? (
                      <span className="co-pill co-pill-mock">PENDING</span>
                    ) : isRejected ? (
                      <span className="co-pill co-pill-failed">REJECTED</span>
                    ) : (
                      <span className={w.suspended ? "co-pill co-pill-overdue" : "co-pill co-pill-paid"}>
                        {w.suspended ? "SUSPENDED" : "ACTIVE"}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      {isPending ? (
                        <>
                          <button
                            disabled={busy === w.id}
                            onClick={() => approve(w)}
                            data-testid={`platform-approve-${w.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#15803D] hover:bg-[#166534] text-white text-xs font-semibold rounded-sm co-btn-press disabled:opacity-50"
                          >
                            <Check className="h-3.5 w-3.5" strokeWidth={2} /> Approve
                          </button>
                          <button
                            disabled={busy === w.id}
                            onClick={() => reject(w)}
                            data-testid={`platform-reject-${w.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#B91C1C] text-[#B91C1C] text-xs font-semibold rounded-sm hover:bg-[#FEF2F2] co-btn-press disabled:opacity-50"
                          >
                            <X className="h-3.5 w-3.5" strokeWidth={2} /> Reject
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            disabled={busy === w.id}
                            onClick={() => {
                              localStorage.setItem("co_impersonate_ws", w.id);
                              localStorage.setItem("co_impersonate_ws_name", w.name);
                              window.location.href = "/app/dashboard";
                            }}
                            data-testid={`platform-impersonate-${w.id}`}
                            className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                            title="View as owner"
                          >
                            <UsersIcon className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} />
                          </button>
                          <button
                            disabled={busy === w.id}
                            onClick={() => toggleSuspend(w)}
                            data-testid={`${w.suspended ? T.platformUnsuspendBtn : T.platformSuspendBtn}-${w.id}`}
                            className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                            title={w.suspended ? "Reactivate" : "Suspend"}
                          >
                            {w.suspended
                              ? <Play className="h-4 w-4 text-[#15803D]" strokeWidth={1.75} />
                              : <Ban className="h-4 w-4 text-[#B45309]" strokeWidth={1.75} />}
                          </button>
                          <button
                            disabled={busy === w.id}
                            onClick={() => remove(w)}
                            data-testid={`${T.platformDeleteBtn}-${w.id}`}
                            className="p-2 hover:bg-[#FEF2F2] rounded-sm transition-colors duration-200"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4 text-[#B91C1C]" strokeWidth={1.75} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
